import { useRef, useState } from "react";
import api, { avatarSrc, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Camera, Trash2, Upload } from "lucide-react";
import { logSwallow } from "../../lib/log";

/**
 * AvatarUpload — square, tap-to-upload, remove-to-clear.
 *
 * Privacy note: this component only shows the CURRENT user's own avatar (it
 * uses whatever `user.avatar_url` /auth/me returned). Public views of the
 * same user's avatar go through public_profile() on the backend which already
 * filters out `avatar_url` for Ghost-mode viewers without an opt-in reveal.
 */
export default function AvatarUpload({ user, onUpdated, size = 96, hint = true }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = avatarSrc(user);

  const pick = () => inputRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPEG, PNG, or WebP images");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Image must be under 3MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "avatar");
      const { data } = await api.post("/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Refresh /auth/me so consumers pick up the new URL + updated_at cache-bust
      try {
        const me = await api.get("/auth/me");
        onUpdated?.(me.data.user);
      } catch (e) { logSwallow("avatar.refresh.me", e); }
      toast.success("Profile picture updated");
      return data;
    } catch (e) {
      toast.error(fmtErr(e));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!src) return;
    if (!window.confirm("Remove your profile picture?")) return;
    setBusy(true);
    try {
      await api.delete("/avatar");
      try {
        const me = await api.get("/auth/me");
        onUpdated?.(me.data.user);
      } catch (e) { logSwallow("avatar.refresh.me", e); }
      toast.success("Profile picture removed");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const dimStyle = { width: size, height: size };

  return (
    <div className="flex items-center gap-4" data-testid="avatar-upload">
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        style={dimStyle}
        className="relative rounded-full overflow-hidden border-2 border-white/10 bg-gradient-to-br from-[#1a0000] to-black flex items-center justify-center hover:border-[#E60000]/50 transition disabled:opacity-60"
        data-testid="avatar-picker"
      >
        {src ? (
          <img src={src} alt="Your avatar" className="w-full h-full object-cover" data-testid="avatar-image" />
        ) : (
          <Camera size={Math.max(20, size / 3)} className="text-zinc-500" />
        )}
        <span className="absolute inset-0 bg-black/0 hover:bg-black/40 transition flex items-center justify-center opacity-0 hover:opacity-100">
          <Upload size={18} className="text-white" />
        </span>
      </button>
      <div className="flex-1 min-w-0">
        {hint && (
          <p className="text-xs text-zinc-500 leading-relaxed">
            {src
              ? "Tap the photo to change it. Others only see it in Visible mode or after you reveal to them."
              : "Tap to upload. JPEG / PNG / WebP up to 3MB."}
          </p>
        )}
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={pick}
            disabled={busy}
            data-testid="avatar-upload-btn"
            className="text-xs font-mono-accent tracking-wider text-white/90 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30 transition disabled:opacity-50"
          >
            {busy ? "…" : src ? "CHANGE" : "UPLOAD"}
          </button>
          {src && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              data-testid="avatar-remove-btn"
              className="text-xs font-mono-accent tracking-wider text-zinc-500 hover:text-[#FF1A1A] px-3 py-1.5 rounded-full border border-white/10 hover:border-[#FF1A1A]/30 transition disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Trash2 size={12} /> REMOVE
            </button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid="avatar-file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
